import { InformationCircleIcon } from '@heroicons/react/16/solid';
import { useCallback, useState } from 'react';
import { useModalState } from '../../hooks/useModalState';
import { useProductLeverage } from '../../hooks/useProductLeverage';
import { ProductType } from '../../hooks/useProducts';
import { usePropsAsState } from '../../hooks/usePropAsState';
import { formatNumberWithDecimals } from '../../lib/formatters';
import { Gwei, GWEI_UNIT } from '../../lib/gwei';
import { leverageSliderTooltip } from '../../lib/tooltips';
import { Badge } from '../catalyst-ui-kit/badge';
import { Button } from '../catalyst-ui-kit/button';
import { Input } from '../Input/Input';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { LeverageSlider } from '../StyledSlider/LeverageSlider';
import { Tooltip } from '../Tooltip/Tooltip';

export type LeverageProps = {
  leverage: number;
  maxLeverage: number;
  baseTokenName: string;
  productLabel: string;
  setLeverage: (leverage: number) => void;
};

export const LeverageModalUi: React.FC<LeverageProps> = ({
  leverage,
  maxLeverage,
  productLabel,
  setLeverage,
}) => {
  const isOpen = useModalState((state) => state.leverageModal === 'open');
  const setLeverageModal = useModalState((state) => state.setLeverageModal);
  // This is used to let users empty the input but still maintain a min of 1x.
  // If input field is empty on blur, we set it to the last valid value.
  const [tempInputValue, setTempInputValue] = useState<string | undefined>(undefined);
  const [stagedValue, setStagedValue] = usePropsAsState(Gwei.toGwei(leverage));

  const handleAdjustLeverage = () => {
    setLeverage(Gwei.fromGwei(stagedValue));
    setLeverageModal('closed');
  };
  const handleClose = useCallback(() => {
    setLeverageModal('closed');
    // Reset slider
    setStagedValue(Gwei.toGwei(leverage));
  }, [setLeverageModal, setStagedValue, leverage]);

  return (
    <ResponsiveModal
      onClose={handleClose}
      title={
        <span className="flex items-center gap-2">
          Adjust Leverage {productLabel}{' '}
          <Tooltip content={leverageSliderTooltip()}>
            <InformationCircleIcon width={16} height={16} />
          </Tooltip>
        </span>
      }
      open={isOpen}
    >
      <div className="select-none">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-contentPrimary">{productLabel}</span>
            <Badge
              className="cursor-pointer"
              onClick={() => {
                setStagedValue(Gwei.toGwei(maxLeverage));
              }}
            >
              Max {maxLeverage}x
            </Badge>
          </div>
          <Input
            min={1}
            max={maxLeverage}
            label="Leverage"
            inputType="number"
            value={tempInputValue ?? Gwei.fmt(stagedValue)}
            step={1}
            decimalScale={1}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (floatValue === 0) return false;

              return (floatValue || 0) <= maxLeverage;
            }}
            onBlur={() => {
              setTempInputValue(undefined);
            }}
            defaultValue={1}
            suffix="x"
            onValueChange={(values) => {
              if (values.floatValue === undefined) {
                setTempInputValue(values.value);
                return;
              }
              if (tempInputValue !== undefined) {
                setTempInputValue(undefined);
              }
              setStagedValue(Gwei.toGwei(values.value));
            }}
          />
          <LeverageSlider
            className="mt-4"
            value={stagedValue}
            onChange={(value) => setStagedValue(Gwei.max(BigInt(GWEI_UNIT), value))}
            formatValue={(x) => formatNumberWithDecimals(x) + 'x'}
            max={Gwei.toGwei(maxLeverage)}
          />
        </div>
        <div className="mb-6 mt-8 text-[10px] text-contentSecondary">
          The leverage setting doesn't change your liquidation price or position margin - it only
          limits your leverage as you enter the position.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button color="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button name="Adjust Leverage" onClick={handleAdjustLeverage} color="blue">
            Confirm
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export type LeverageModalProps = {
  product: ProductType;
};

export const LeverageModal: React.FC<LeverageModalProps> = ({ product }) => {
  const { value: leverage, setValue: setLeverage } = useProductLeverage(product);
  return (
    <LeverageModalUi
      leverage={leverage}
      maxLeverage={product.maxLeverage}
      baseTokenName={product.baseTokenName}
      productLabel={product.displayTicker}
      setLeverage={setLeverage}
    />
  );
};
